import { z } from 'zod';

/**
 * Client-side mirror of the contact-form validation schema.
 *
 * IMPORTANT — why this is a separate copy instead of a shared import from
 * `api/_lib/validation.ts`:
 *
 * 1. Both `vercel dev` (local) and production Vercel deployments treat every
 *    request under `/api/*` as belonging exclusively to the serverless
 *    function router. Non-function files such as `api/_lib/validation.ts`
 *    are correctly excluded from becoming endpoints, but they are also
 *    **not served as static/module files** under that same routing layer.
 *    A browser-side `import` that resolves to a URL under `/api/` (e.g. via
 *    a `@api/*` alias) therefore 404s outside of plain `vite`-only dev
 *    (`npm run dev`), which breaks the whole module graph and prevents the
 *    app from rendering at all.
 * 2. `api/send-inquiry.ts` is a locked, known-good file; it must not be
 *    modified, and `api/_lib/validation.ts` must keep using safe,
 *    same-directory relative imports only (no cross-directory imports into
 *    `src/`), per past incidents documented inline in `send-inquiry.ts`.
 *
 * Keep this schema in sync with `api/_lib/validation.ts` whenever the
 * contact-form fields or limits change.
 */

/** Maximum accepted length (in characters) for each field. */
const FIELD_LIMITS = {
    fullName: 100,
    email: 254, // max length of an email address per RFC 5321
    phone: 40,
    subject: 150,
    message: 5000,
} as const;

/**
 * Pragmatic email check. Intentionally simple and dependency-version agnostic
 * (avoids relying on a specific schema-library email implementation).
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Schema describing a valid inquiry payload. Field names match the API
 * contract (`Full_Name`, `_replyto`, ...) used by `api/send-inquiry.ts`.
 */
export const contactInquirySchema = z.object({
    Full_Name: z
        .string()
        .trim()
        .min(1, 'Full name is required.')
        .max(FIELD_LIMITS.fullName, `Full name must be at most ${FIELD_LIMITS.fullName} characters.`),
    _replyto: z
        .string()
        .trim()
        .min(3, 'A valid email address is required.')
        .max(FIELD_LIMITS.email, `Email must be at most ${FIELD_LIMITS.email} characters.`)
        .regex(EMAIL_PATTERN, 'A valid email address is required.'),
    Phone_Number: z
        .string()
        .trim()
        .max(FIELD_LIMITS.phone, `Phone number must be at most ${FIELD_LIMITS.phone} characters.`)
        .optional()
        .default(''),
    _subject: z
        .string()
        .trim()
        .min(1, 'A subject is required.')
        .max(FIELD_LIMITS.subject, `Subject must be at most ${FIELD_LIMITS.subject} characters.`),
    Message: z
        .string()
        .trim()
        .min(1, 'A message is required.')
        .max(FIELD_LIMITS.message, `Message must be at most ${FIELD_LIMITS.message} characters.`),
});

/** Validated, normalized inquiry payload. */
export type ContactInquiry = z.infer<typeof contactInquirySchema>;
